/**
 * FinderIconView Component
 *
 * Grid of icons with GIF hover animation support.
 */

import { useState, useCallback } from 'react'
import { ContentItem } from './FinderWindow'
import './finder.css'

interface FinderIconViewProps {
  items: ContentItem[]
  selectedItem: ContentItem | null
  onItemSelect: (item: ContentItem) => void
  onItemDoubleClick?: (item: ContentItem) => void
}

// Default icons based on item properties
const getDefaultIcon = (item: ContentItem): string => {
  // Check for type-specific icons
  if (item.type === 'founded' || item.type === 'co-founded') return '🚀'
  if (item.type === 'employment') return '💼'

  // Check for proficiency level
  if (item.proficiency === 'expert') return '⭐'
  if (item.proficiency === 'advanced') return '🔷'

  // Check for category
  if (item.category === 'cto') return '👑'
  if (item.category === 'infrastructure') return '🖥️'
  if (item.category === 'developer') return '💻'
  if (item.category === 'product') return '💡'
  if (item.category === 'founder') return '🚀'

  return '📄'
}

// Get display name for item
const getDisplayName = (item: ContentItem): string => {
  return item.name || item.company || item.title || item.slug
}

// Get subtitle for item
const getSubtitle = (item: ContentItem): string | null => {
  if (item.role) return item.role as string
  if (item.years) return `${item.years} years`
  if (item.proficiency) return item.proficiency as string
  return null
}

export function FinderIconView({
  items,
  selectedItem,
  onItemSelect,
  onItemDoubleClick,
}: FinderIconViewProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)

  const handleClick = useCallback(
    (item: ContentItem, e: React.MouseEvent) => {
      e.preventDefault()
      onItemSelect(item)
    },
    [onItemSelect]
  )

  const handleDoubleClick = useCallback(
    (item: ContentItem, e: React.MouseEvent) => {
      e.preventDefault()
      onItemDoubleClick?.(item)
    },
    [onItemDoubleClick]
  )

  const handleKeyDown = useCallback(
    (item: ContentItem, e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onItemSelect(item)
      }
    },
    [onItemSelect]
  )

  if (items.length === 0) {
    return (
      <div className="finder-icon-view finder-icon-view--empty">
        <p>No items in this category</p>
      </div>
    )
  }

  return (
    <div className="finder-icon-view">
      <div className="finder-icon-grid">
        {items.map((item) => {
          const isSelected = selectedItem?.slug === item.slug
          const isHovered = hoveredItem === item.slug
          const displayName = getDisplayName(item)
          const subtitle = getSubtitle(item)
          const icon = item.icon || getDefaultIcon(item)

          // Check if icon is an image path or emoji
          const isImageIcon =
            icon.endsWith('.png') ||
            icon.endsWith('.jpg') ||
            icon.endsWith('.gif') ||
            icon.endsWith('.svg')

          // Check for GIF thumbnail
          const hasGif = item.thumbnail?.endsWith('.gif')
          const showGif = hasGif && isHovered

          return (
            <button
              key={item.slug}
              className={`finder-icon-item ${isSelected ? 'selected' : ''}`}
              onClick={(e) => handleClick(item, e)}
              onDoubleClick={(e) => handleDoubleClick(item, e)}
              onKeyDown={(e) => handleKeyDown(item, e)}
              onMouseEnter={() => setHoveredItem(item.slug)}
              onMouseLeave={() => setHoveredItem(null)}
              aria-selected={isSelected}
              role="option"
            >
              <div className="finder-icon-item__icon">
                {isImageIcon ? (
                  <img
                    src={showGif && item.thumbnail ? item.thumbnail : `/icons/${icon}`}
                    alt=""
                    className="finder-icon-item__img"
                    loading="lazy"
                  />
                ) : (
                  <span className="finder-icon-item__emoji">{icon}</span>
                )}
              </div>
              <div className="finder-icon-item__label">
                <span className="finder-icon-item__name">{displayName}</span>
                {subtitle && (
                  <span className="finder-icon-item__subtitle">{subtitle}</span>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default FinderIconView
